/**
 * Interests Explorer - Student Pathways GPS
 * Copying LinkedIn's interest selection + AI-powered career matching
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Sparkles, Heart, Briefcase, Code, Palette, Music,
  Wrench, Users, TrendingUp, Book, Globe, Lightbulb,
  Save, ArrowRight, Brain, Target, Star, Loader2, ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import Navigation from '@/components/Navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Interest categories with icons
const INTEREST_CATEGORIES = [
  { id: 'technology', name: 'Technology & Computers', icon: Code, emoji: '💻', careers: ['Software Developer', 'Web Designer', 'IT Support', 'Data Analyst'] },
  { id: 'business', name: 'Business & Finance', icon: TrendingUp, emoji: '📈', careers: ['Business Analyst', 'Financial Advisor', 'Marketing Manager', 'Entrepreneur'] },
  { id: 'healthcare', name: 'Healthcare & Medicine', icon: Heart, emoji: '🏥', careers: ['Nurse', 'Medical Assistant', 'Physical Therapist', 'Pharmacist'] },
  { id: 'education', name: 'Education & Teaching', icon: Book, emoji: '📚', careers: ['Teacher', 'Counselor', 'Tutor', 'Education Coordinator'] },
  { id: 'creative', name: 'Arts & Design', icon: Palette, emoji: '🎨', careers: ['Graphic Designer', 'Photographer', 'Video Editor', 'Artist'] },
  { id: 'music', name: 'Music & Entertainment', icon: Music, emoji: '🎵', careers: ['Musician', 'Audio Engineer', 'Event Planner', 'DJ'] },
  { id: 'trades', name: 'Skilled Trades', icon: Wrench, emoji: '🔧', careers: ['Electrician', 'Plumber', 'HVAC Tech', 'Carpenter'] },
  { id: 'social', name: 'Social Services', icon: Users, emoji: '🤝', careers: ['Social Worker', 'Youth Counselor', 'Community Organizer', 'Case Manager'] },
  { id: 'stem', name: 'Science & Engineering', icon: Brain, emoji: '🔬', careers: ['Engineer', 'Scientist', 'Lab Technician', 'Researcher'] },
  { id: 'hospitality', name: 'Hospitality & Tourism', icon: Globe, emoji: '🌍', careers: ['Hotel Manager', 'Chef', 'Travel Agent', 'Event Coordinator'] }
];

const InterestsExplorer = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [aiCareerSuggestions, setAiCareerSuggestions] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSavedInterests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSavedInterests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/interests`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedInterests(data.interests || []);
        setSelectedInterests(data.interests || []);
      }
    } catch (error) {
      console.error('Error fetching interests:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleInterest = (interestId) => {
    setSelectedInterests(prev =>
      prev.includes(interestId)
        ? prev.filter(i => i !== interestId)
        : [...prev, interestId]
    );
  };

  const saveInterests = async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/interests`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ interests: selectedInterests })
      });

      if (response.ok) {
        setSavedInterests(selectedInterests);
        toast.success('Interests saved! 🎯');
        
        // Auto-generate AI career suggestions
        if (selectedInterests.length >= 2) {
          getAICareerSuggestions();
        }
      } else {
        toast.error('Failed to save interests');
      }
    } catch (error) {
      toast.error('Error saving interests');
    }
  };

  const getAICareerSuggestions = async () => {
    if (selectedInterests.length === 0) {
      toast.error('Select at least one interest first');
      return;
    }

    setLoadingAI(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/career-guidance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          interests: selectedInterests,
          skills: [],
          education_level: 'high_school'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setAiCareerSuggestions(data.careers || []);
        toast.success('AI found career matches for you! ✨');
      } else {
        toast.error('AI career guidance unavailable');
      }
    } catch (error) {
      console.error('AI error:', error);
      toast.error('Failed to get career suggestions');
    } finally {
      setLoadingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F3EF' }}>
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div className="p-6 md:p-8 rounded-b-3xl shadow-lg mb-6" style={{ background: colors.header_gradient || colors.header }}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <Lightbulb className="w-10 h-10 text-white" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white" style={{ fontFamily: 'var(--font-heading)' }}>Explore Interests</h1>
          </div>
          <p className="text-lg text-white/90">Discover what you're passionate about and find careers that match! 🚀</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">
        {/* Interest Selection */}
        <Card className="p-6 mb-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
          <h2 className="text-xl font-bold mb-4" style={{ color: colors.text }}>What interests you?</h2>
          <p className="text-sm mb-6" style={{ color: colors.text }}>Select all that apply - the more you choose, the better our AI can match you with careers!</p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {INTEREST_CATEGORIES.map(category => (
              <motion.div
                key={category.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  className={`p-4 cursor-pointer transition-all ${
                    selectedInterests.includes(category.id) ? 'ring-2 ring-blue-500' : ''
                  }`}
                  style={{
                    backgroundColor: selectedInterests.includes(category.id) 
                      ? 'rgba(59,130,246,0.1)' 
                      : colors.surface,
                    borderColor: selectedInterests.includes(category.id) ? '#3B82F6' : colors.border
                  }}
                  onClick={() => toggleInterest(category.id)}
                >
                  <div className="text-center">
                    <div className="text-3xl mb-2">{category.emoji}</div>
                    <p className="text-sm font-bold" style={{ color: colors.text }}>{category.name}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="flex gap-3 mt-6">
            <Button
              onClick={saveInterests}
              disabled={selectedInterests.length === 0}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-gray-900 font-semibold"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Interests ({selectedInterests.length})
            </Button>
            {selectedInterests.length >= 2 && (
              <Button
                onClick={getAICareerSuggestions}
                disabled={loadingAI}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-gray-900 font-semibold"
              >
                {loadingAI ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    AI Matching...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Get AI Career Matches
                  </>
                )}
              </Button>
            )}
          </div>
        </Card>

        {/* AI Career Suggestions */}
        {aiCareerSuggestions && aiCareerSuggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card
              className="p-6"
              style={{
                backgroundColor: 'rgba(139,92,246,0.03)',
                borderColor: '#8B5CF6',
                borderWidth: '2px'
              }}
            >
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-6 h-6" style={{ color: '#8B5CF6' }} />
                <h2 className="text-2xl font-bold" style={{ color: colors.text }}>AI Career Matches</h2>
                <Badge className="bg-purple-500/20 text-purple-600 font-semibold">Powered by GPT-4o</Badge>
              </div>

              <div className="space-y-4">
                {aiCareerSuggestions.map((career, idx) => (
                  <Card
                    key={idx}
                    className="p-5"
                    style={{ backgroundColor: colors.card, borderColor: colors.border }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                        💼
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold mb-2" style={{ color: colors.text }}>{career.title}</h3>
                        <p className="text-sm mb-3" style={{ color: colors.text }}>{career.description}</p>
                        
                        <div className="grid md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <p className="text-xs font-bold mb-1" style={{ color: colors.text }}>Skills Needed:</p>
                            <div className="flex flex-wrap gap-1">
                              {career.skills_needed?.slice(0, 3).map((skill, i) => (
                                <Badge key={i} className="bg-blue-500/10 text-blue-600 font-semibold text-xs">
                                  {skill}
                                </Badge>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-bold mb-1" style={{ color: colors.text }}>Education:</p>
                            <p className="text-xs font-semibold" style={{ color: colors.text }}>{career.education_path}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-bold" style={{ color: colors.primary }}>
                            💰 {career.salary_range}
                          </p>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-gray-900 font-semibold"
                            onClick={() => navigate('/skills')}
                          >
                            Explore Path <ArrowRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </motion.div>
        )}

        {/* Selected Interests Summary */}
        {selectedInterests.length > 0 && (
          <Card className="p-6 mt-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.text }}>Your Interests ({selectedInterests.length})</h3>
            <div className="flex flex-wrap gap-2">
              {selectedInterests.map(id => {
                const category = INTEREST_CATEGORIES.find(c => c.id === id);
                return (
                  <Badge key={id} className="bg-blue-500/10 text-blue-600 px-3 py-2 font-semibold">
                    {category?.emoji} {category?.name}
                  </Badge>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      <Navigation user={user} />
    </div>
  );
};

export default InterestsExplorer;
